// KPI içe aktarma sihirbazı — adım şeridi + yerel durum (M16 T11; D8 async job, kural 7-9).
// Durum store'a GİRMEZ: job/mapping/kolon/önizleme burada tutulur, sunucu taslağı
// (`PUT /mapping`) kalıcı kopyadır. Tamamlanınca `refreshGrid()` (+ kural 8: yıl aralığı genişler).

import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ClientEntity,
  KpiImportColumn,
  KpiMappingDefaults,
  KpiMappingRow,
  KpiResolvedGrid,
} from "@mahir/shared";
import {
  INTERNAL_IMPORT_API,
  type ImportApi,
  type KpiConflictPolicy,
  type KpiImportCommitResult,
  type KpiImportJobDto,
  type KpiImportPreview,
} from "../../../api/kpi-import";
import { savableMapping } from "../../../lib/kpi-import-breakdown";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import {
  columnsFromGrid,
  gridRowCount,
  initialMapping,
  stepForJobStatus,
  yearsOutsideView,
} from "../../../lib/kpi-import-wizard";
import { useKpiStore } from "../../../stores/kpi-store";
import { ImportCommitStep } from "./ImportCommitStep";
import { ImportMappingStep } from "./ImportMappingStep";
import { ImportPreviewStep } from "./ImportPreviewStep";
import { ImportUploadStep } from "./ImportUploadStep";

export type WizardStep = 1 | 2 | 3 | 4;

export interface ImportWizardProps {
  clientId: string;
  setId: string;
  /** Sihirbaz aktifken (job var, aktarılmadı) ContextBar kilidi (kural 9). */
  onActiveChange: (active: boolean) => void;
  /** T19 — API tabanı (varsayılan iç `/kpi`; portal `PORTAL_IMPORT_API`). */
  api?: ImportApi;
  /**
   * T19 — müşteri modu: `create` seçeneği ve işletme kolonu düzenleme YOK, AI Kontrolü paneli
   * render edilmez, 400/403 hataları anlaşılır metin; grid bağlamı `gridCtx`'ten (portal store).
   */
  isClient?: boolean;
  gridCtx?: {
    years: number[];
    currentGrid: KpiResolvedGrid | null;
    entities: ClientEntity[];
    refreshGrid: () => Promise<void>;
    setYears: (years: number[]) => Promise<void>;
  };
  /** Tamamlandı adımındaki bağlantı kökü (iç `/clients/kpi`, portal `/portal/kpi`). */
  basePath?: string;
}

function ImportStepper({ step }: { step: WizardStep }): JSX.Element {
  return (
    <ol
      className="flex items-center gap-2 flex-wrap mb-5"
      style={{ listStyle: "none", padding: 0 }}
    >
      {KPI_IMPORT_COPY.steps.map((label, i) => {
        const n = (i + 1) as WizardStep;
        const done = n < step;
        const active = n === step;
        return (
          <li key={label} className="flex items-center gap-2">
            <span
              aria-current={active ? "step" : undefined}
              className="flex items-center gap-2 text-sm"
              style={{
                color: active ? "var(--color-text)" : "var(--color-text-3)",
                fontWeight: active ? 600 : 400,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  background: done || active ? "var(--color-primary)" : "var(--color-bg)",
                  color: done || active ? "var(--color-surface)" : "var(--color-text-3)",
                  border: done || active ? "none" : "1px solid var(--color-border)",
                }}
              >
                {done ? "✓" : n}
              </span>
              <span className="sr-only">
                {done ? "tamamlandı: " : active ? "geçerli adım: " : ""}
              </span>
              {label}
            </span>
            {i < KPI_IMPORT_COPY.steps.length - 1 ? (
              <span
                aria-hidden="true"
                style={{ width: 56, height: 1, background: "var(--color-border)", margin: "0 4px" }}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export function ImportWizard(props: ImportWizardProps): JSX.Element {
  const { clientId, setId, onActiveChange, gridCtx } = props;
  const api = props.api ?? INTERNAL_IMPORT_API;
  const isClient = props.isClient ?? false;
  const basePath = props.basePath ?? "/clients/kpi";
  const [step, setStep] = useState<WizardStep>(1);
  const [job, setJob] = useState<KpiImportJobDto | null>(null);
  const [mapping, setMapping] = useState<KpiMappingRow[]>([]);
  const [columns, setColumns] = useState<KpiImportColumn[][]>([]);
  // B-02/B-03 — dosyada olmayan yıl/işletme; taslakla sunucuda saklanır
  const [defaults, setDefaults] = useState<KpiMappingDefaults>({});
  const [preview, setPreview] = useState<KpiImportPreview | null>(null);
  const [policy, setPolicy] = useState<KpiConflictPolicy>("keep_existing");
  const [result, setResult] = useState<KpiImportCommitResult | null>(null);
  const [committedAt, setCommittedAt] = useState<string | null>(null);
  const [yearsNote, setYearsNote] = useState<{ outside: number[]; view: number[] } | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resumeKey, setResumeKey] = useState(0);

  // İç akışta kpi-store; portalda `gridCtx` (hooks koşulsuz — store müşteri için boş kalır).
  const storeYears = useKpiStore((s) => s.years);
  const storeRefreshGrid = useKpiStore((s) => s.refreshGrid);
  const storeSetYears = useKpiStore((s) => s.setYears);
  const storeGrid = useKpiStore((s) => s.grid);
  const viewYears = gridCtx?.years ?? storeYears;
  const refreshGrid = gridCtx?.refreshGrid ?? storeRefreshGrid;
  const setYears = gridCtx?.setYears ?? storeSetYears;
  const currentGrid = gridCtx ? gridCtx.currentGrid : storeGrid;

  const reset = useCallback((): void => {
    setStep(1);
    setJob(null);
    setMapping([]);
    setColumns([]);
    setDefaults({});
    setPreview(null);
    setPolicy("keep_existing");
    setResult(null);
    setCommittedAt(null);
    setYearsNote(null);
    setResumeError(null);
    setResumeKey((k) => k + 1);
  }, []);

  // Müşteri/set değişince sihirbaz sıfırlanır (taslak sunucuda durur).
  useEffect(() => {
    reset();
  }, [clientId, setId, reset]);

  const active = job !== null && step < 4;
  useEffect(() => {
    onActiveChange(active);
    return () => onActiveChange(false);
  }, [active, onActiveChange]);

  /** Job `ready` (grid + öneri var) → eşleştirme adımı. */
  const enterMapping = useCallback((ready: KpiImportJobDto): void => {
    if (!ready.grid) return;
    setJob(ready);
    setMapping(initialMapping(ready.grid, ready.mapping));
    setColumns(columnsFromGrid(ready.grid));
    setDefaults({
      ...(ready.defaultYear !== null ? { defaultYear: ready.defaultYear } : {}),
      ...(ready.defaultEntityId !== null ? { defaultEntityId: ready.defaultEntityId } : {}),
      // P2-6/P2-8 — sayfa bazlı yıl + atlanan sayfalar taslaktan gelir
      ...(Object.keys(ready.defaultYearBySheet ?? {}).length > 0
        ? { defaultYearBySheet: ready.defaultYearBySheet }
        : {}),
      ...((ready.skippedSheets ?? []).length > 0 ? { skippedSheets: ready.skippedSheets } : {}),
    });
    setPreview(null);
    setStep(2);
  }, []);

  /** Taslaktan devam (D8): tam job çekilir, durumuna göre adım seçilir. */
  const resume = useCallback(
    async (jobId: string): Promise<void> => {
      setResumeError(null);
      try {
        const full = await api.getJob(jobId);
        const target = stepForJobStatus(full.status);
        if (target === 2 && full.grid && gridRowCount(full.grid) > 0) {
          enterMapping(full);
        } else {
          setJob(full);
          setStep(1);
        }
      } catch (err) {
        setResumeError(err instanceof Error ? err.message : KPI_IMPORT_COPY.genericError);
      }
    },
    [enterMapping, api],
  );

  const onCommitted = useCallback(
    (res: KpiImportCommitResult, usedPreview: KpiImportPreview): void => {
      setResult(res);
      setCommittedAt(new Date().toISOString());
      const outside = yearsOutsideView(usedPreview.plan, viewYears);
      if (outside.length > 0) {
        const all = [...viewYears, ...outside];
        const from = Math.min(...all);
        const to = Math.max(...all);
        const next: number[] = [];
        for (let y = from; y <= to; y += 1) next.push(y);
        setYearsNote({ outside, view: next });
        void setYears(next); // yıl aralığı genişler + grid tazelenir (kural 8)
      } else {
        void refreshGrid();
      }
      setStep(4);
    },
    [viewYears, setYears, refreshGrid],
  );

  const fileLine = useMemo(() => (job ? job.fileName : null), [job]);

  return (
    <div>
      {fileLine ? (
        <div className="text-sm mb-3" style={{ color: "var(--color-text-2)" }}>
          {fileLine}
        </div>
      ) : null}
      <ImportStepper step={step} />
      {resumeError ? (
        <div role="alert" className="mb-3 text-sm" style={{ color: "var(--color-error)" }}>
          {resumeError}
        </div>
      ) : null}

      {step === 1 ? (
        <ImportUploadStep
          key={resumeKey}
          clientId={clientId}
          setId={setId}
          job={job}
          onJob={setJob}
          onReady={enterMapping}
          onResume={(id) => void resume(id)}
          onCancel={reset}
          api={api}
          isClient={isClient}
        />
      ) : null}

      {step === 2 && job?.grid ? (
        <ImportMappingStep
          job={job}
          grid={job.grid}
          clientId={clientId}
          setId={setId}
          mapping={mapping}
          columns={columns}
          onMapping={setMapping}
          onColumns={setColumns}
          defaults={defaults}
          onDefaults={setDefaults}
          api={api}
          isClient={isClient}
          entities={gridCtx?.entities}
          onBack={() => setStep(1)}
          onNext={() => {
            setPreview(null);
            setStep(3);
          }}
        />
      ) : null}

      {step === 3 && job ? (
        <ImportPreviewStep
          job={job}
          mapping={mapping}
          columns={columns}
          defaults={defaults}
          preview={preview}
          onPreview={setPreview}
          policy={policy}
          onPolicy={setPolicy}
          api={api}
          isClient={isClient}
          currentGrid={currentGrid}
          {...(isClient
            ? {}
            : {
                onMappingChange: (next: KpiMappingRow[]) => {
                  setMapping(next);
                  setPreview(null);
                  void api
                    .saveMapping(job.id, savableMapping(next), columns, defaults)
                    .catch(() => undefined);
                },
              })}
          onBack={() => setStep(2)}
          onCommitted={onCommitted}
        />
      ) : null}

      {step === 4 && job && result ? (
        <ImportCommitStep
          job={job}
          result={result}
          committedAt={committedAt ?? new Date().toISOString()}
          yearsNote={yearsNote}
          onNewFile={reset}
          basePath={basePath}
        />
      ) : null}
    </div>
  );
}
